import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { batchesAPI, tutorsAPI, resolveImage, uploadAPI , translationAPI} from '../utils/api';
import { useTranslation } from 'react-i18next';



// Get the correct language string from a bilingual field
import { tField } from '../utils/bilingualHelper';

const LEVEL_BADGE = { Beginner:'badge-blue', Intermediate:'badge-orange', Advanced:'badge-gold' };

const parse12h = (str) => {
  if (!str) return { h: '09', m: '00', p: 'AM' };
  const parts = str.split(' ');
  const timePart = parts[0];
  let period = parts[1];
  let [h, m] = timePart.split(':');
  let hour = parseInt(h, 10);
  let min = m || '00';
  if (!period) {
    if (hour >= 12) { period = 'PM'; if (hour > 12) hour -= 12; }
    else { period = 'AM'; if (hour === 0) hour = 12; }
  }
  return { h: hour.toString().padStart(2, '0'), m: min.toString().padStart(2, '0'), p: period.toUpperCase() };
};

const format12h = (h, m, p) => `${h}:${m} ${p}`;
const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
const MINS  = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

function BatchModal({ onClose, onSave, batch = null }) {
  const { t, i18n } = useTranslation();
  const isEdit = !!batch;
  const startObj = parse12h(batch?.startTime);
  const endObj   = parse12h(batch?.endTime);

  const [tutors, setTutors] = useState([]);
  const [masterLevels, setMasterLevels] = useState([]);
  const [allMerchandise, setAllMerchandise] = useState([]);
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(
    // If editing and ZH already exists, mark as translated
    !!(batch?.name?.zh || batch?.category?.zh || batch?.shortDesc?.zh)
  );
  const [form, setForm] = useState(batch ? { 
    ...batch, 
    name: typeof batch.name === 'string' ? { en: batch.name, zh: '' } : (batch.name || { en: '', zh: '' }),
    category: typeof batch.category === 'string' ? { en: batch.category, zh: '' } : (batch.category || { en: '', zh: '' }),
    shortDesc: typeof batch.shortDesc === 'string' ? { en: batch.shortDesc, zh: '' } : (batch.shortDesc || { en: '', zh: '' }),
    tutor: batch.tutor?._id || batch.tutor?.id || batch.tutor,
    offeredMerchandise: batch.offeredMerchandise?.map(m => m._id || m) || [],
    termDiscountPercent: batch.termDiscountPercent !== undefined ? batch.termDiscountPercent : 20,
    isAdvertised: batch.isAdvertised !== undefined ? batch.isAdvertised : true,
    isPopular: batch.isPopular !== undefined ? batch.isPopular : false
  } : { 
    name:{ en:'', zh:'' }, category:{ en:'', zh:'' }, shortDesc:{ en:'', zh:'' }, level:'Beginner', tutor:'', maxStudents:20, feeAmount: 1800, days:['Mon'], startDate:'', endDate:'', termDiscountPercent: 20, isAdvertised: true, isPopular: false, offeredMerchandise: []
  });

  // Translate ALL bilingual text fields in one shot
  const handleTranslateAll = async () => {
    const fieldsToTranslate = ['name', 'category', 'shortDesc'];
    
    const missing = fieldsToTranslate.filter(f => !form[f]?.en?.trim() && !form[f]?.zh?.trim());
    if (missing.length > 0) return alert(`Please fill in at least one language for: ${missing.join(', ')} before translating.`);

    setTranslating(true);
    try {
      const results = await Promise.all(
        fieldsToTranslate.map(f => {
          const enStr = form[f]?.en?.trim();
          const zhStr = form[f]?.zh?.trim();
          
          if (zhStr && !enStr) {
             // Translate ZH -> EN
             return translationAPI.translate(zhStr, 'zh-TW', 'en')
              .then(d => ({ field: f, en: d.responseData?.translatedText || '' }))
              .catch(() => ({ field: f }));
          } else {
             // Translate EN -> ZH (default)
             return translationAPI.translate(enStr, 'en', 'zh-TW')
              .then(d => ({ field: f, zh: d.responseData?.translatedText || '' }))
              .catch(() => ({ field: f }));
          }
        })
      );
      const updates = {};
      results.forEach((res) => { 
         if (res.zh) updates[res.field] = { ...form[res.field], zh: res.zh }; 
         if (res.en) updates[res.field] = { ...form[res.field], en: res.en }; 
      });
      setForm(p => ({ ...p, ...updates }));
      setTranslated(true);
    } catch { alert('Translation failed. Please try again.'); }
    finally { setTranslating(false); }
  };

  const [startTime, setStartTime] = useState(startObj);
  const [endTime, setEndTime]     = useState(endObj);
  const [loading, setLoad] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(batch?.coverImage ? resolveImage(batch.coverImage) : '');

  useEffect(() => {
    Promise.all([
      tutorsAPI.getAll(),
      require('../utils/api').settingsAPI.get(),
      require('../utils/api').merchandiseAPI.getAll()
    ]).then(([tRes, sRes, mRes]) => {
      setTutors(tRes.data);
      setMasterLevels(sRes.data.masterLevels || ['Beginner', 'Intermediate', 'Advanced']);
      setAllMerchandise(mRes.data.items || []);
    });
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    // ── Validation ──────────────────────────────────────────────
    if (!form.name?.en?.trim()) return alert('Batch name (English) is required.');
    if (form.name.en.trim().length < 3) return alert('Batch name must be at least 3 characters.');
    if (!form.category?.en?.trim()) return alert('Category / Tag (English) is required.');
    if (!form.shortDesc?.en?.trim()) return alert('Short description (English) is required.');
    if (!form.name?.zh?.trim() || !form.category?.zh?.trim() || !form.shortDesc?.zh?.trim()) {
      return alert('Please click ✨ Translate All to Chinese before saving.');
    }
    if (!form.tutor) return alert('Please select a handling instructor for this batch.');
    if (!form.level) return alert('Please select an experience level for this batch.');
    if (!form.days || form.days.length === 0) return alert('Please select at least one schedule day.');
    if (!form.feeAmount || Number(form.feeAmount) <= 0) return alert('Please enter a valid fee amount (must be greater than 0).');
    if (!form.maxStudents || Number(form.maxStudents) <= 0) return alert('Max students must be a positive number.');
    if (form.termDiscountPercent < 0 || form.termDiscountPercent > 100) return alert('Term discount must be between 0 and 100.');
    if (!form.startDate) return alert('Start date is required.');
    if (!form.endDate) return alert('End date is required.');
    if (form.startDate && form.endDate && form.endDate < form.startDate) return alert('End date cannot be before start date.');
    if (!isEdit && !imageFile) return alert('Please upload a batch cover image.');
    // ────────────────────────────────────────────────────────────
    setLoad(true);
    let finalCoverImage = form.coverImage;
    try {
      if (imageFile) {
        const formData = new FormData();
        formData.append('photo', imageFile);
        const upRes = await uploadAPI.batchImage(formData);
        finalCoverImage = upRes.data.url;
      }
      const payload = {
        ...form,
        coverImage: finalCoverImage,
        startTime: format12h(startTime.h, startTime.m, startTime.p),
        endTime: format12h(endTime.h, endTime.m, endTime.p)
      };
      if (isEdit) await batchesAPI.update(batch._id, payload);
      else await batchesAPI.create(payload);
      onSave();
      onClose();
    } catch { alert('Failed to save batch.'); } finally { setLoad(false); }
  };

  const toggleDay = (day) => {
    setForm(p => ({
      ...p,
      days: p.days.includes(day) ? p.days.filter(d => d !== day) : [...p.days, day]
    }));
  };

  const toggleMerchandise = (mId) => {
    setForm(p => {
      const current = p.offeredMerchandise || [];
      return {
        ...p,
        offeredMerchandise: current.includes(mId) ? current.filter(id => id !== mId) : [...current, mId]
      };
    });
  };

  return (
    <div className="dash-modal-overlay">
      <div className="dash-modal" style={{ maxWidth: 520 }}>
        <div className="dash-modal-header">
          <div className="dash-modal-title">{isEdit ? t('batches.modalUpdateTitle') : t('batches.modalCreateTitle')}</div>
          <button className="dash-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="dash-modal-body">
          {/* ── English-only inputs (clean single fields) ── */}
          <div className="form-group">
            <label className="form-label-style">{t('batches.thName')} <span style={{color:'#888',fontWeight:400,fontSize:11}}>{t('batches.englishSub')}</span></label>
            <input className="form-input-style" placeholder="e.g. Morning Ballet" value={form.name?.en || ''} onChange={e => { setForm(p=>({...p, name:{...p.name, en:e.target.value}})); setTranslated(false); }} />
          </div>
          <div className="form-group">
            <label className="form-label-style">{t('batches.categoryLabel')} <span style={{color:'#888',fontWeight:400,fontSize:11}}>{t('batches.englishSub')}</span></label>
            <input className="form-input-style" placeholder="e.g. Urban, Classical" value={form.category?.en || ''} onChange={e => { setForm(p=>({...p, category:{...p.category, en:e.target.value}})); setTranslated(false); }} />
          </div>
          <div className="form-group">
            <label className="form-label-style">{t('batches.descLabel')} <span style={{color:'#888',fontWeight:400,fontSize:11}}>{t('batches.englishSub')}</span></label>
            <textarea className="form-input-style" rows={2} placeholder="e.g. Master classical ballet postures and grace." value={form.shortDesc?.en || ''} onChange={e => { setForm(p=>({...p, shortDesc:{...p.shortDesc, en:e.target.value}})); setTranslated(false); }} />
          </div>

          {/* ── One-click Translate All button ── */}
          <div style={{ background: translated ? 'rgba(76,175,134,0.08)' : 'rgba(123,29,58,0.06)', border: `1px solid ${translated ? '#4caf86' : 'rgba(123,29,58,0.2)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              {translated ? (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#4caf86', marginBottom: 4 }}>✅ Chinese Translation Ready</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    <span style={{ marginRight: 16 }}>{t('batches.nameLabelZH')}: <b>{form.name?.zh || '—'}</b></span>
                    <span style={{ marginRight: 16 }}>{t('batches.catLabelZH')}: <b>{form.category?.zh || '—'}</b></span>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{t('batches.descLabelZH')}: {form.shortDesc?.zh || '—'}</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#7b1d3a', marginBottom: 2 }}>🌐 Translation Required</div>
                  <div style={{ fontSize: 12, color: '#888' }}>Fill fields in either language above, then click to auto-translate.</div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleTranslateAll}
              disabled={translating}
              style={{ background: translated ? '#4caf86' : 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: translating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: translating ? 0.7 : 1, minWidth: 120 }}
            >
              {translating ? '⏳ Translating...' : translated ? '🔄 Re-translate' : '✨ Translate All'}
            </button>
          </div>
          <div className="form-group">
            <label className="form-label-style">{t('batches.coverImg')}</label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: 80, height: 60, borderRadius: 4, background: '#eee', backgroundImage: `url(${imagePreview})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <input type="file" accept="image/*" onChange={handleImageChange} />
            </div>   
          </div> 
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, marginBottom: 16 }}>
            <input 
              type="checkbox"  
              id="isAdvertisedList" 
              style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--wine)' }} 
              checked={!!form.isAdvertised} 
              onChange={e => setForm(p=>({...p,isAdvertised: e.target.checked}))} 
            />
            <label htmlFor="isAdvertisedList" style={{ fontSize: 13, fontWeight: 600, color: '#333', cursor: 'pointer', userSelect: 'none' }}>
              📢 {t('batches.advertiseLabel')}
            </label>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: -8, marginBottom: 16 }}>
            <input 
              type="checkbox" 
              id="isPopularList" 
              style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--wine)' }} 
              checked={!!form.isPopular} 
              onChange={e => setForm(p=>({...p,isPopular: e.target.checked}))} 
            />
            <label htmlFor="isPopularList" style={{ fontSize: 13, fontWeight: 600, color: '#333', cursor: 'pointer', userSelect: 'none' }}>
              ✨ {t('batches.popularLabel')}
            </label>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label-style">{t('batches.startDate')}</label>
              <input type="date" className="form-input-style" value={form.startDate || ''} onChange={e => setForm(p=>({...p,startDate:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('batches.endDate')}</label>
              <input type="date" className="form-input-style" value={form.endDate || ''} onChange={e => setForm(p=>({...p,endDate:e.target.value}))} />
            </div>
          </div>
          <div className="form-row-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label-style">{t('batches.thLevel')}</label>
              <select className="form-input-style" value={form.level} onChange={e => setForm(p=>({...p,level:e.target.value}))}>
                {masterLevels.map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label-style">{t('batches.thCapacity')}</label>
              <input type="number" className="form-input-style" value={form.maxStudents} onChange={e => setForm(p=>({...p,maxStudents:Number(e.target.value)}))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label-style">{t('batches.fee')}</label>
              <input type="number" className="form-input-style" value={form.feeAmount} onChange={e => setForm(p=>({...p,feeAmount:Number(e.target.value)}))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label-style">{t('batches.discount')}</label>
              <input type="number" className="form-input-style" placeholder="20" min="0" max="100" value={form.termDiscountPercent !== undefined ? form.termDiscountPercent : 20} onChange={e => setForm(p=>({...p,termDiscountPercent:Number(e.target.value)}))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label-style">{t('batches.instructor')}</label>
            <select className="form-input-style" value={form.tutor} onChange={e => setForm(p=>({...p,tutor:e.target.value}))}>
              <option value="">{t('batches.selectTutor')}</option>
              {tutors.map(tutor => <option key={tutor._id} value={tutor._id}>{tField(tutor.name, i18n.language)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label-style">{t('batches.days')}</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                <button key={d} className={`badge ${form.days.includes(d) ? 'badge-gold' : ''}`} style={{ cursor:'pointer', border:'none' }} onClick={() => toggleDay(d)}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label-style">{t('batches.merch')}</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {allMerchandise.map(m => {
                const mId = m.id || m._id;
                return (
                  <button 
                    key={mId} 
                    className={`badge ${(form.offeredMerchandise || []).includes(mId) ? 'badge-blue' : ''}`} 
                    style={{ cursor:'pointer', border:'1px solid #ccc', background: (form.offeredMerchandise || []).includes(mId) ? undefined : '#f9f9f9', color: (form.offeredMerchandise || []).includes(mId) ? undefined : '#666' }} 
                    onClick={() => toggleMerchandise(mId)}
                    type="button"
                  >
                    {tField(m.name, i18n.language)} (${m.price})
                  </button>
                );
              })}
            </div>
            {allMerchandise.length === 0 && <div style={{ fontSize: 12, color: '#888' }}>No merchandise items found. Add them in settings.</div>}
          </div>
          
          <div className="form-row-2" style={{ marginTop: 10 }}>
            <div className="form-group">
              <label className="form-label-style">{t('batches.startTime')}</label>
              <div className="time-picker-container">
                <div className="time-select-wrapper">
                  <select className="time-unit-select" value={startTime.h} onChange={e => setStartTime(p=>({...p,h:e.target.value}))}>
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="time-colon">:</span>
                  <select className="time-unit-select" value={startTime.m} onChange={e => setStartTime(p=>({...p,m:e.target.value}))}>
                    {MINS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="ampm-toggle-pill">
                  <button type="button" className={`ampm-btn ${startTime.p === 'AM' ? 'active' : ''}`} onClick={() => setStartTime(p=>({...p,p:'AM'}))}>AM</button>
                  <button type="button" className={`ampm-btn ${startTime.p === 'PM' ? 'active' : ''}`} onClick={() => setStartTime(p=>({...p,p:'PM'}))}>PM</button>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label-style">{t('batches.endTime')}</label>
              <div className="time-picker-container">
                <div className="time-select-wrapper">
                  <select className="time-unit-select" value={endTime.h} onChange={e => setEndTime(p=>({...p,h:e.target.value}))}>
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="time-colon">:</span>
                  <select className="time-unit-select" value={endTime.m} onChange={e => setEndTime(p=>({...p,m:e.target.value}))}>
                    {MINS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="ampm-toggle-pill">
                  <button type="button" className={`ampm-btn ${endTime.p === 'AM' ? 'active' : ''}`} onClick={() => setEndTime(p=>({...p,p:'AM'}))}>AM</button>
                  <button type="button" className={`ampm-btn ${endTime.p === 'PM' ? 'active' : ''}`} onClick={() => setEndTime(p=>({...p,p:'PM'}))}>PM</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="dash-modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>{t('batches.cancel')}</button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading || !translated}>
            {loading ? 'Saving...' : !translated ? '⚠️ Translate First' : 'Save Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Batches() {
  const { t, i18n } = useTranslation();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState({ show: false, batch: null });
  const navigate = useNavigate();

  const fetchIdRef = React.useRef(0);

  const load = (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    setError('');
    batchesAPI.getAll()
      .then(r => {
        if (isMounted && currentId === fetchIdRef.current) setBatches(Array.isArray(r.data) ? r.data : []);
      })
      .catch(err => {
        if (isMounted && currentId === fetchIdRef.current) setError(err.response?.data?.message || 'Failed to load batches. Please try again.');
      })
      .finally(() => {
        if (isMounted && currentId === fetchIdRef.current) setLoading(false);
      });
  };

  useEffect(() => {
    let isMounted = true;
    load(isMounted);
    return () => { isMounted = false; };
  }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Strict Confirmation: Permanently remove this batch and all its schedule records?')) return;
    try {
      await batchesAPI.delete(id);
      setBatches(p => p.filter(b => b._id !== id));
    } catch { alert('Delete failed.'); }
  };

  return (
    <Layout title={t('batches.title')}>
      {modal.show && <BatchModal batch={modal.batch} onClose={() => setModal({ show: false, batch: null })} onSave={load} />}

      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('batches.title')}</h1>
          <p className="text-sm text-muted">{t('batches.subtitle')}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({ show: true, batch: null })}>{t('batches.createBtn')}</button>
      </div>

      <div className="grid-3" style={{ gap:24 }}>
        {loading ? (
          <div className="p-8">⏳ Loading schedule...</div>
        ) : error ? (
          <div className="dash-empty" style={{ gridColumn: '1 / -1', padding: '60px 20px' }}>
            <div className="dash-empty-icon">⚠️</div>
            <div className="dash-empty-title">{t('batches.errLoad')}</div>
            <p className="dash-empty-sub">{error}</p>
            <button className="btn btn-primary btn-sm" onClick={load} style={{ marginTop: 16 }}>{t('batches.retry')}</button>
          </div>
        ) : batches.length === 0 ? (
          <div className="dash-empty" style={{ gridColumn: '1 / -1', padding: '60px 20px' }}>
            <div className="dash-empty-icon">🏢</div>
            <div className="dash-empty-title">{t('batches.noBatches')}</div>
            <p className="dash-empty-sub">{t('batches.clickToCreate')}</p>
          </div>
        ) : batches.map(b => (
          <div key={b._id} className="dash-card batch-card relative group" style={{ cursor:'pointer', padding: 0, overflow: 'hidden' }} onClick={() => navigate(`/batches/${b._id}`)}>
            <div style={{ 
              height: 130, 
              backgroundImage: `url(${resolveImage(b.coverImage) || 'https://images.pexels.com/photos/8463000/pexels-photo-8463000.jpeg?auto=compress&cs=tinysrgb&w=600'})`, 
              backgroundSize: 'cover', 
              backgroundPosition: 'center',
              position: 'relative'
            }}>
              <div style={{ position:'absolute', top:12, right:12, display:'flex', gap:6 }}>
                 <button className="btn btn-xs btn-outline bg-white" onClick={(e) => { e.stopPropagation(); setModal({ show: true, batch: b }); }}>Edit</button>
                 <button className="btn btn-xs btn-outline bg-white" style={{ color:'#e05252' }} onClick={(e) => handleDelete(b._id, e)}>✕</button>
              </div>
            </div>
            <div style={{ padding: 20 }}>
              <div className="flex-between mb-4">
                <span className={`badge ${LEVEL_BADGE[b.level]}`}>{b.level}</span>
                <span className="text-sm text-muted">👥 {b.studentCount || 0} / {b.maxStudents}</span>
              </div>
              <h3 className="dash-card-title mb-4" style={{ fontSize:18 }}>{tField(b.name, i18n.language)}</h3>
              <div className="dash-schedule-strip mb-6">
                <div style={{ fontWeight:700, fontSize:13 }}>{Array.isArray(b.days) ? b.days.join(', ') : (b.days || '—')}</div>
                <div style={{ fontSize:12, color:'rgba(0,0,0,.6)' }}>{b.startTime} - {b.endTime}</div>
                {b.startDate && b.endDate && (
                  <div style={{ fontSize:11, color:'var(--wine)', fontWeight:600, marginTop:4 }}>📅 {b.startDate} to {b.endDate}</div>
                )}
              </div>
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:12, display:'flex', alignItems:'center', gap:10 }}>
                <div className="dash-avatar" style={{ width:30, height:30, backgroundImage: b.tutor?.photoUrl ? `url("${resolveImage(b.tutor.photoUrl)}")` : 'none' }}>{!b.tutor?.photoUrl && tField(b.tutor?.name, i18n.language)?.[0]}</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{tField(b.tutor?.name, i18n.language) || t('batches.noTutor')}</div>
                  {b.tutor?.email && <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{b.tutor.email}</div>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
